import { Box, Typography } from '@mui/material';

import { Card, Section, StatusChip } from './Card';
import { COLORS, MUSCLE_STATUS_LABELS } from './constants';
import { formatNumber, isNum } from './format';

const STATUS_TONE = { below: 'neutral', in_range: 'good', above: 'caution', none: 'neutral' };

// A usable range needs a finite min and a larger finite max; anything else is ignored.
export const validRange = (range) =>
  isNum(range?.min) && isNum(range?.max) && range.max > range.min
    ? { min: range.min, max: range.max }
    : null;

export const MuscleRow = ({ group, fallbackRange = null }) => {
  const range = validRange(group.targetRange) || fallbackRange;
  const sets = isNum(group.weeklyEffectiveSets) ? group.weeklyEffectiveSets : null;
  const scaleMax = Math.max(range ? range.max * 1.5 : 0, sets || 0, 1);
  const pct = (value) => `${Math.max(0, Math.min(100, (value / scaleMax) * 100))}%`;
  const statusLabel = MUSCLE_STATUS_LABELS[group.status] || MUSCLE_STATUS_LABELS.none;
  const summary =
    sets === null
      ? `${group.label}: no data yet`
      : `${group.label}: ${formatNumber(sets)} weekly sets, ${
          range ? `target ${range.min} to ${range.max}, ` : ''
        }${statusLabel}`;
  return (
    <Box component="li" sx={{ listStyle: 'none' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
        <Typography sx={{ fontWeight: 600, fontSize: 15 }}>{group.label}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: 14 }}>
            {sets === null ? '--' : `${formatNumber(sets)} sets/wk`}
          </Typography>
          <StatusChip label={statusLabel} tone={STATUS_TONE[group.status]} />
        </Box>
      </Box>
      <Box
        role="meter"
        aria-label={summary}
        aria-valuemin={0}
        aria-valuemax={Math.ceil(scaleMax)}
        aria-valuenow={sets === null ? 0 : Math.min(sets, Math.ceil(scaleMax))}
        sx={{
          position: 'relative',
          mt: 0.75,
          height: 12,
          borderRadius: 1,
          backgroundColor: 'rgba(255,255,255,0.08)',
        }}
      >
        {range && (
          <Box
            data-testid="target-band"
            sx={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: pct(range.min),
              width: `calc(${pct(range.max)} - ${pct(range.min)})`,
              backgroundColor: 'rgba(76,217,100,0.25)',
              border: `1px dashed ${COLORS.good}`,
            }}
          />
        )}
        {sets !== null && (
          <Box
            sx={{
              position: 'absolute',
              top: 3,
              bottom: 3,
              left: 0,
              width: pct(sets),
              borderRadius: 1,
              backgroundColor: COLORS.accent,
            }}
          />
        )}
      </Box>
    </Box>
  );
};

const MuscleBalance = ({ muscleGroups }) => {
  const groups = muscleGroups || [];
  // Shared range for the intro text and for rows whose own range is missing or partial.
  const range = groups.map((g) => validRange(g.targetRange)).find(Boolean) || null;
  return (
    <Section title="Muscle balance" id="perf-muscles">
      <Card>
        <Typography sx={{ fontSize: 14, opacity: 0.8, mb: 1 }}>
          Weekly effective sets per muscle group.
          {range &&
            ` The shaded band is the ${range.min} to ${range.max} set range often suggested for building muscle.`}
        </Typography>
        <Typography sx={{ fontSize: 14, opacity: 0.8, mb: 2 }}>
          Volume is a guide, not a score. The machine trains your upper body more by design, so
          lower numbers for some muscles are normal.
        </Typography>
        <Box component="ul" sx={{ p: 0, m: 0, display: 'grid', gap: 2 }}>
          {groups.map((group) => (
            <MuscleRow key={group.key} group={group} fallbackRange={range} />
          ))}
        </Box>
      </Card>
    </Section>
  );
};

export default MuscleBalance;
